import React, { useEffect } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { createComponentLogger } from '@/utils/logger';
import { isValidInternalRedirect } from '@/utils/authRedirect';

const log = createComponentLogger('AuthRedirect', 'authentication');

/**
 * AuthRedirect Component
 *
 * Handles redirect flow for authenticated routes, typically used in email links.
 * If user is logged in, redirects to target. If not, redirects to login with return URL.
 *
 * Security: Only allows redirects to /internal/* paths (prevents open redirect).
 *
 * Usage: /auth/redirect?to=/internal/conditions/abc-123
 */
export const AuthRedirect: React.FC = () => {
  const { user, loading } = useAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const rawTo = searchParams.get('to') || '/internal';

  // SECURITY: Only allow internal paths (prevent open redirect)
  const to = isValidInternalRedirect(rawTo) ? rawTo : '/internal';

  useEffect(() => {
    if (loading) {
return;
}

    if (user) {
      // Already logged in, redirect immediately
      log.debug('User authenticated, redirecting to:', to);
      window.location.href = to;
    } else {
      // Need to log in, go to login with redirect
      log.debug('User not authenticated, redirecting to login with return URL');
      window.location.href = `/?redirect=${encodeURIComponent(to)}`;
    }
  }, [user, loading, to]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Redirecting...</p>
      </div>
    </div>
  );
};
